import { useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import { usePlaylistDetail } from './usePlaylistDetail';
import { playUrlForPlaylistItem, playlistContentTypeLabel, withPlaylistQueryParams } from './playlistPaths';
import type { PlaylistContentType } from './api';

export function PlaylistPanel({
  sessionContentId,
  sessionContentType,
}: {
  sessionContentId: string;
  sessionContentType: PlaylistContentType;
}) {
  const location = useLocation();
  const history = useHistory();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const playlistId = params.get('playlistId');
  const playlistItemId = params.get('playlistItemId');

  const { data } = usePlaylistDetail(playlistId ?? undefined, !!playlistId);

  const sorted = useMemo(() => {
    if (!data?.items) return [];
    return [...data.items].sort((a, b) => a.position - b.position);
  }, [data?.items]);

  const isActiveItem = (
    item: (typeof sorted)[number],
  ) => {
    if (playlistItemId) return item.id === playlistItemId;
    return (
      item.contentId === sessionContentId &&
      item.contentType === sessionContentType
    );
  };

  if (!playlistId || !data) return null;

  return (
    <Box>
      <Box sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: '24px', lineHeight: '24px', fontWeight: 'bold', letterSpacing: '0.3px' }} noWrap>
          {data.name}
        </Typography>
        <Typography sx={{ fontSize: '13px', lineHeight: '148%', letterSpacing: '0.17px', color: 'text.secondary', mt: 0.5 }}>
          {sorted.length} sesje
        </Typography>
      </Box>
      <List dense disablePadding>
        {sorted.map((item, idx) => {
          const url = withPlaylistQueryParams(
            playUrlForPlaylistItem(item),
            playlistId,
            item.id,
          );
          const active = isActiveItem(item);
          return (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => history.push(url)}
                sx={{
                  gap: 1,
                  alignItems: 'flex-start',
                  py: 0.75,
                  ...(active && {
                    bgcolor: 'grey.100',
                    '&:hover': { bgcolor: 'grey.200' },
                  }),
                }}
              >
                {item.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={item.thumbnailUrl}
                    alt=""
                    sx={{
                      width: 72,
                      height: 40,
                      objectFit: 'cover',
                      flexShrink: 0,
                      borderRadius: 0.5,
                      mt: 0.25,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72,
                      height: 40,
                      flexShrink: 0,
                      bgcolor: 'action.hover',
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.25,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {idx + 1}
                    </Typography>
                  </Box>
                )}
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '16px',
                      lineHeight: '23px',
                      letterSpacing: '0.3px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                  secondary={playlistContentTypeLabel(item.contentType)}
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: '11px',
                      lineHeight: '14px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    },
                  }}
                  sx={{ my: 0 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
